const NEWLINE = "\n";
const SPACE = 32;

// The server writes exactly one zero-width rectangle at the right end of every text line it extracts.
export function isLineEnd(rectangle: number[]) {
  return rectangle[2] === 0;
}

// The number of lines the rectangles make: each ends at a line end, and the last one may have none.
function countRectangleLines(rectangles: number[][]) {
  let lines = 0;
  for (const rectangle of rectangles) if (isLineEnd(rectangle)) lines++;
  return isLineEnd(rectangles[rectangles.length - 1]) ? lines : lines + 1;
}

// The number of lines the text makes: each ends at a newline, and the last one may have none.
function countTextLines(text: string) {
  let lines = 0;
  let start = 0;
  for (let end = text.indexOf(NEWLINE); end >= 0; end = text.indexOf(NEWLINE, start)) {
    lines++;
    start = end + 1;
  }
  return start < text.length ? lines + 1 : lines;
}

// Pairing by position, which is only right while every rectangle has a character of its own.
function alignByIndex(text: string, count: number) {
  const characters = new Array<string>(count);
  for (let index = 0; index < count; index++) characters[index] = text.charAt(index);
  characters[count - 1] += text.slice(count);
  return characters;
}

/**
 * Where the rectangles without a character sit in a line, as the number of characters before each one, in order. The
 * line is the `length` characters of `text` from `start`. The text around a tick box reads `"Yes  No"` or ends `" \n"`,
 * so a rectangle without a character sits after a space (or at the start of the line) and before a space (or at the
 * end of it). With more such places than rectangles the last ones are taken, and a rectangle with no such place goes
 * at the end of the line. The places are looked for from the end of the line, as the last ones are the ones wanted.
 */
function findGaps(text: string, start: number, length: number, count: number) {
  const gaps: number[] = [];
  for (let gap = length; gap >= 0 && gaps.length < count; gap--) {
    const afterSpace = gap === 0 || text.charCodeAt(start + gap - 1) === SPACE;
    const beforeSpace = gap === length || text.charCodeAt(start + gap) === SPACE;
    if (afterSpace && beforeSpace) gaps.push(gap);
  }

  gaps.reverse();
  while (gaps.length < count) gaps.push(length);
  return gaps;
}

/**
 * Writes the characters of a line into `count` rectangles of `characters`, from `first`. The line is the `length`
 * characters of `text` from `start`, without its newline.
 */
function writeCharacters(
  characters: string[],
  first: number,
  count: number,
  text: string,
  start: number,
  length: number,
) {
  if (count < length) {
    // The characters the rectangles cannot hold stay with the last one, so no text is lost.
    for (let index = 0; index < count - 1; index++) characters[first + index] = text.charAt(start + index);
    characters[first + count - 1] = text.slice(start + count - 1, start + length);
    return;
  }

  if (count === length) {
    for (let index = 0; index < count; index++) characters[first + index] = text.charAt(start + index);
    return;
  }

  const gaps = findGaps(text, start, length, count - length);
  let gap = 0;
  let next = 0;
  for (let index = 0; index < count; index++) {
    if (gap < gaps.length && gaps[gap] === next) {
      characters[first + index] = "";
      gap++;
    } else {
      characters[first + index] = text.charAt(start + next++);
    }
  }
}

/**
 * The text of each rectangle: `alignText(text, rectangles)[i]` belongs to `rectangles[i]`, and the result joins back
 * to `text`. A rectangle without a character (such as a tick box: the server adds the rectangle but no text) makes the
 * text shorter than the rectangles, so pairing them by position puts every later character on the wrong rectangle.
 * Each text line ends with a newline and a line end rectangle, so the lines are paired one by one and the error stays
 * within the line it is in. A rectangle without a character gets `""`.
 *
 * A line with more rectangles than characters is placed by `findGaps`, which is exact on the real pages this was
 * checked on. A tick box that is not surrounded by spaces, or a stray double space elsewhere on the line, puts the
 * gap at the wrong rectangle of that line, never of another one. When the text and the rectangles do not split into
 * the same number of lines nothing says which line a character belongs to, and they are paired by position.
 *
 * The lines are walked once, writing straight into the result: no line is split off the text or the rectangles.
 */
export function alignText(text: string, rectangles: number[][]) {
  const total = rectangles.length;
  if (total === 0) return [];
  if (countRectangleLines(rectangles) !== countTextLines(text)) return alignByIndex(text, total);

  const characters = new Array<string>(total);
  let first = 0;
  let start = 0;
  while (first < total) {
    // The line has the rectangles from `first` to `last`, the last of which is its line end unless it is cut short
    let last = first;
    while (last < total - 1 && !isLineEnd(rectangles[last])) last++;
    const hasLineEnd = isLineEnd(rectangles[last]);
    const count = last - first + (hasLineEnd ? 0 : 1);

    const newlineAt = text.indexOf(NEWLINE, start);
    const end = newlineAt < 0 ? text.length : newlineAt;
    const newline = newlineAt < 0 ? "" : NEWLINE;

    if (count === 0) {
      // Nothing but the line end to hold the line
      characters[first] = text.slice(start, end) + newline;
    } else {
      writeCharacters(characters, first, count, text, start, end - start);
      if (hasLineEnd) characters[last] = newline;
      else characters[last] += newline;
    }

    first = last + 1;
    start = end + newline.length;
  }
  return characters;
}
